import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import AddButton from "../../../../components/admin/usable/AddButton";
import CancelButton from "../../../../components/admin/usable/CancelButton";
import EditButton from "../../../../components/admin/usable/actions/EditButton";
import DeleteButton from "../../../../components/admin/usable/actions/DeleteButton";
import TableLoader from "../../../../components/admin/usable/TableLoader";
import Switch from "@mui/joy/Switch";

import {
  viewNeighbourhood,
  addNeighbourhood,
  updateNeighbourhood,
  deleteNeighbourhood,
} from "../../../../api/admin/neighbourhoodApi";

import { viewCountry } from "../../../../api/admin/countryApi";
import { viewDivision } from "../../../../api/admin/divisionApi";
import { viewCity } from "../../../../api/admin/cityApi";

const Neighbourhood = () => {
  const [NeighbourhoodData, setNeighbourhoodData] = useState([]);
  const [CountryData, setCountryData] = useState([]);
  const [DivisionData, setDivisionData] = useState([]);
  const [CityData, setCityData] = useState([]);

  const [Loading, setLoading] = useState(true);
  const [Submitting, setSubmitting] = useState(false);

  const [FormData, setFormData] = useState({
    id: null,
    neighbourhoodName: "",
    countryId: "",
    divisionId: "",
    cityId: "",
    status: 1,
  });

  useEffect(() => {
    viewNeighbourhood(setNeighbourhoodData, setLoading);
    viewCountry(setCountryData);
    viewDivision(setDivisionData);
    viewCity(setCityData);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => {
      if (id === "countryId") {
        return {
          ...prev,
          countryId: value,
          divisionId: "",
          cityId: "",
        };
      }

      if (id === "divisionId") {
        return {
          ...prev,
          divisionId: value,
          cityId: "",
        };
      }

      return {
        ...prev,
        [id]: value,
      };
    });
  };

  const handleStatusChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked ? 1 : 0,
    }));
  };

  const resetForm = () => {
    setFormData({
      id: null,
      neighbourhoodName: "",
      countryId: "",
      divisionId: "",
      cityId: "",
      status: 1,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!FormData.neighbourhoodName.trim()) {
      toast.error("Neighbourhood name is required");
      return;
    }

    if (!FormData.countryId) {
      toast.error("Please select a country");
      return;
    }

    if (!FormData.divisionId) {
      toast.error("Please select a division");
      return;
    }

    if (!FormData.cityId) {
      toast.error("Please select a city");
      return;
    }

    const payload = {
      ...FormData,
      neighbourhoodName: FormData.neighbourhoodName.trim(),
      countryId: Number(FormData.countryId),
      divisionId: Number(FormData.divisionId),
      cityId: Number(FormData.cityId),
      status: Number(FormData.status),
    };

    setSubmitting(true);

    try {
      if (FormData.id) {
        await updateNeighbourhood(
          payload,
          setFormData,
          setNeighbourhoodData,
          setLoading,
        );
      } else {
        await addNeighbourhood(
          payload,
          setFormData,
          setNeighbourhoodData,
          setLoading,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      neighbourhoodName: item.neighbourhoodName || "",
      countryId: item.countryId ?? "",
      divisionId: item.divisionId ?? "",
      cityId: item.cityId ?? "",
      status: Number(item.status),
    });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this neighbourhood?")) {
      deleteNeighbourhood(id, setNeighbourhoodData, setLoading);
    }
  };

  const FilteredDivisions = DivisionData.filter(
    (item) => Number(item.countryId) === Number(FormData.countryId),
  );

  const FilteredCities = CityData.filter(
    (item) =>
      Number(item.countryId) === Number(FormData.countryId) &&
      Number(item.divisionId) === Number(FormData.divisionId),
  );

  return (
    <div className="w-full bg-white border rounded-lg p-4">
      <div className="py-3">
        <p className="font-bold">Neighbourhood</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="neighbourhoodName" className="block mb-2">
              Neighbourhood Name
            </label>

            <input
              type="text"
              id="neighbourhoodName"
              value={FormData.neighbourhoodName}
              onChange={handleChange}
              placeholder="Enter neighbourhood name"
              className="w-full border rounded-md px-3 py-2 focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="countryId" className="block mb-2">
              Country
            </label>

            <select
              id="countryId"
              value={FormData.countryId}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none"
            >
              <option value="">Select Country</option>

              {CountryData.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.countryName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="divisionId" className="block mb-2">
              Division
            </label>

            <select
              id="divisionId"
              value={FormData.divisionId}
              onChange={handleChange}
              disabled={!FormData.countryId}
              className="w-full border rounded-md px-3 py-2 focus:outline-none disabled:bg-gray-100"
            >
              <option value="">Select Division</option>

              {FilteredDivisions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.divisionName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="cityId" className="block mb-2">
              City
            </label>

            <select
              id="cityId"
              value={FormData.cityId}
              onChange={handleChange}
              disabled={!FormData.divisionId}
              className="w-full border rounded-md px-3 py-2 focus:outline-none disabled:bg-gray-100"
            >
              <option value="">Select City</option>

              {FilteredCities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.cityName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-2">Status</label>

            <div className="flex items-center gap-2">
              <Switch
                checked={Number(FormData.status) === 1}
                onChange={handleStatusChange}
                slotProps={{
                  input: {
                    "aria-label": "Neighbourhood status",
                  },
                }}
              />

              <span>
                {Number(FormData.status) === 1 ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <AddButton
            type="submit"
            disabled={Submitting}
            text={Submitting ? "Submitting..." : FormData.id ? "Update" : "Add"}
          />

          <CancelButton onClick={resetForm} text={"Reset"} />
        </div>
      </form>

      <div className="overflow-x-auto pt-5">
        <table className="w-full border-collapse border">
          <thead>
            <tr>
              <th className="border p-2 text-center">SL</th>
              <th className="border p-2 text-center">Neighbourhood Name</th>
              <th className="border p-2 text-center">Country</th>
              <th className="border p-2 text-center">Division</th>
              <th className="border p-2 text-center">City</th>
              <th className="border p-2 text-center">Status</th>
              <th className="border p-2 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {Loading ? (
              <TableLoader colSpan={7} />
            ) : NeighbourhoodData.length > 0 ? (
              NeighbourhoodData.map((item, index) => (
                <tr key={item.id}>
                  <td className="border p-2 text-center">{index + 1}</td>

                  <td className="border p-2 text-center">
                    {item.neighbourhoodName}
                  </td>

                  <td className="border p-2 text-center">
                    {item.countryName ||
                      CountryData.find(
                        (country) =>
                          Number(country.id) === Number(item.countryId),
                      )?.countryName ||
                      "N/A"}
                  </td>

                  <td className="border p-2 text-center">
                    {item.divisionName ||
                      DivisionData.find(
                        (division) =>
                          Number(division.id) === Number(item.divisionId),
                      )?.divisionName ||
                      "N/A"}
                  </td>

                  <td className="border p-2 text-center">
                    {item.cityName ||
                      CityData.find(
                        (city) => Number(city.id) === Number(item.cityId),
                      )?.cityName ||
                      "N/A"}
                  </td>

                  <td className="border p-2 text-center">
                    <span
                      className={`px-2 py-1 ${
                        Number(item.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      } rounded text-xs`}
                    >
                      {Number(item.status) === 1 ? "active" : "inactive"}
                    </span>
                  </td>

                  <td className="border p-2 text-center">
                    <div className="flex gap-2 justify-center">
                      <EditButton onClick={() => handleEdit(item)} />
                      <DeleteButton onClick={() => handleDelete(item.id)} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  No neighbourhoods found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Neighbourhood;
